import { useState } from "react"
import { Tv2, LayoutGrid, MonitorSpeaker, Download } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import SubHeader from "../layout/SubHeader"
import ManageRooms from "./ManageRooms"
import BoardRoom from "./BoardRoom"
import DownloadCenter from "./DownloadCenter"

type RoomsTab = "manage" | "download" | "boardroom"

const TABS: { id: RoomsTab; label: string; icon: React.ReactNode; desc: string }[] = [
  { id: "manage",    label: "Manage Rooms",   icon: <LayoutGrid      className="w-4 h-4" />, desc: "View and configure rooms" },
  { id: "boardroom", label: "Board Rooms",    icon: <MonitorSpeaker  className="w-4 h-4" />, desc: "Conference hardware" },
  { id: "download",  label: "Download Center",icon: <Download        className="w-4 h-4" />, desc: "Get InMeet Room app" },
]

export default function RoomsPage() {
  const [activeTab, setActiveTab] = useState<RoomsTab>("manage")

  return (
    <div className="flex p-3 flex-col w-full h-full">
      <SubHeader
        moduleName="Rooms"
        description=""
        OptionalAction={false}
      />
      <div className="flex-1 p-3 min-h-0">
        <div className="flex flex-col md:flex-row h-full w-full bg-card rounded-xl shadow-xl overflow-hidden">

          {/* Desktop Sidebar */}
          <aside className="hidden rounded-l-xl md:flex w-62 shrink-0 flex-col border-r border-stroke-100 dark:border-border overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-2.5 px-4 py-4 border-b border-stroke-100 dark:border-border shrink-0">
              <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
                <Tv2 className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-md font-semibold text-foreground leading-tight">Rooms</p>
                <p className="text-[10px] text-muted-foreground">Conference hardware</p>
              </div>
            </div>

            {/* Nav */}
            <nav className="flex-1 px-2 py-3 space-y-0.5">
              {TABS.map(tab => {
                const active = activeTab === tab.id
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-150 group ${
                      active
                        ? "bg-primary/10 dark:bg-primary/20 text-primary dark:text-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                  >
                    <span className={`shrink-0 ${active ? "text-primary" : "text-muted-foreground group-hover:text-foreground"}`}>
                      {tab.icon}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={`text-xs font-semibold leading-tight ${active ? "text-primary dark:text-foreground" : "text-foreground"}`}>
                        {tab.label}
                      </p>
                      <p className={`text-[10px] truncate mt-0.5 ${active ? "text-primary/70 dark:text-muted-foreground" : "text-muted-foreground"}`}>
                        {tab.desc}
                      </p>
                    </div>
                    {active && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary shrink-0" />}
                  </button>
                )
              })}
            </nav>
          </aside>

          {/* Mobile Header with Title */}
          <div className="md:hidden flex items-center gap-3 px-4 py-3 bg-card border-b border-border shrink-0">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Tv2 className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0">
              <h1 className="text-md font-bold text-slate-900 dark:text-foreground/80 dark:text-white leading-none">Rooms</h1>
            </div>
          </div>

          {/* Mobile Horizontal Tabs */}
          <div className="md:hidden border-b border-border bg-card">
            <div className="rooms-tabs-container flex overflow-x-auto px-4 py-2 gap-1">
              {TABS.map((tab) => {
                const active = activeTab === tab.id
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap shrink-0
                      transition-all duration-150 min-w-fit
                      ${active
                        ? "bg-primary/10 dark:bg-primary/20 text-primary dark:text-foreground"
                        : "text-slate-600 dark:text-muted-foreground hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:text-foreground/80 dark:hover:text-slate-100"
                      }
                    `}
                  >
                    <span className={active ? "text-primary shrink-0" : "text-slate-400 dark:text-foreground shrink-0 transition-colors"}>
                      {tab.icon}
                    </span>
                    <span>{tab.label}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="h-full"
              >
                {activeTab === "manage"    && <ManageRooms />}
                {activeTab === "boardroom" && <BoardRoom />}
                {activeTab === "download"  && <DownloadCenter />}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  )
}
